/**
 * Badge de cuota IA en el sidebar (Sprint 17.4 + hotfix UX mayo 20).
 *
 * Muestra al usuario su plan + cuota IA en el sidebar. Solo se pinta si:
 *
 * 1. Hay un muni activo (daneCode disponible).
 * 2. El plan se cargó exitosamente (no error, no loading).
 *
 * **Diseño post-hotfix:**
 *
 * - Plan **Pro** (cualquier nivel de cuota): label "Asistente IA" + chip
 *   "Pro" + `<QuotaProgressBar>` muda. Cero números a la vista —
 *   tooltip al hover muestra "X de Y usados". Feedback del founder
 *   (mayo 20): los números "150 / 150" generaban ansiedad ("¿me alcanza?")
 *   y fricción. La barra comunica el estado sin presionar.
 * - Plan **Esencial**: chip neutro + CTA upgrade. Sin cuota que mostrar.
 *
 * El query usa el mismo `staleTime: 5min` que la query del expediente,
 * así que comparten cache — abrir un expediente no dispara doble fetch.
 */

import { useQuery } from "@tanstack/react-query";

import { QuotaProgressBar } from "@/components/QuotaProgressBar";
import { fetchMyPlan } from "@/lib/api";

export interface AssistantQuotaBadgeProps {
  /** DANE del muni activo. Si null, el badge no se pinta. */
  daneCode: string | null;
}

export function AssistantQuotaBadge({ daneCode }: AssistantQuotaBadgeProps) {
  const { data, isError } = useQuery({
    queryKey: ["my-plan", daneCode],
    queryFn: () => fetchMyPlan(daneCode as string),
    enabled: Boolean(daneCode),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (!daneCode || isError || !data) return null;

  const { tier, includes_ai_assistant, ai_messages } = data;

  // Esencial — chip neutro con CTA. Sin barra, no aplica.
  if (!includes_ai_assistant) {
    return (
      <div
        data-testid="assistant-quota-badge"
        data-tier="esencial"
        className="mx-2 mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px]"
      >
        <p className="font-semibold tracking-wide text-slate-500 uppercase">Plan Esencial</p>
        <p className="mt-1 text-slate-600">El Asistente IA está disponible en Pro.</p>
        <a
          href="mailto:hola@kaptar.com.co?subject=Quiero%20subir%20a%20Pro"
          className="mt-1.5 inline-block cursor-pointer font-semibold text-brand-700 hover:text-brand-800"
        >
          Pedir upgrade →
        </a>
      </div>
    );
  }

  // Pro: solo header + progress bar. Sin números visibles (tooltip los expone).
  return (
    <div
      data-testid="assistant-quota-badge"
      data-tier={tier}
      className="mx-2 mt-6 rounded-lg border border-slate-200 bg-white p-3"
    >
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] font-semibold tracking-wide text-slate-500 uppercase">
          Asistente IA
        </p>
        <span className="rounded-full bg-brand-700 px-1.5 py-0.5 text-[9px] font-semibold text-white uppercase">
          Pro
        </span>
      </div>
      <QuotaProgressBar quota={ai_messages} widthClass="w-full" />
    </div>
  );
}
